import type { Perfil } from '../entities/Perfil';
import type { RolCanonico } from '../value-objects/Rol';

/**
 * Capacidades del sistema.
 *
 * Cada pantalla y cada acción sensible se pregunta por una de estas, en vez de
 * comparar roles a mano. Cambiar quién puede hacer qué se reduce a editar la
 * matriz de abajo.
 *
 * Esto es defensa en profundidad, no la barrera de seguridad: la autoridad real
 * son las políticas RLS de la base de datos. Ocultar un botón evita errores del
 * usuario; no evita que alguien llame a la API por su cuenta.
 */
export const CAPACIDADES = [
  // --- Acceso a módulos (operación) ---
  'ver_dashboard',
  'ver_alarmas',
  'ver_monitoreo',
  'ver_control',
  'ver_energia',
  'ver_gps',
  'ver_camaras',

  // --- Acceso a módulos (inventario) ---
  'ver_clientes',
  'ver_areas',
  'ver_nodos',
  'ver_sensores',
  'ver_actuadores',
  'ver_medidores',
  'ver_cuentas_ekm',
  'ver_tipos_sensores',
  'ver_tipos_actuadores',
  'ver_grabadores',
  'ver_vistas',

  // --- Acceso a módulos (configuración) ---
  'ver_umbrales',
  'ver_automatizacion',
  'ver_rutas',
  'ver_gps_usuarios',
  'ver_en_ruta',
  'ver_contactos',
  'ver_escalamiento',
  'ver_usuarios',

  // --- Acceso a módulos (sistema) ---
  'ver_outbox',
  'ver_webhooks',
  'ver_bitacora',
  'ver_config_reportes',
  'ver_reportes_generados',

  // --- Acciones ---
  'reconocer_alarmas',
  'resolver_alarmas',
  'operar_actuadores',
  'gestionar_inventario',
  'gestionar_umbrales',
  'gestionar_rutas',
  'gestionar_usuarios',
  'gestionar_notificaciones',
  'gestionar_catalogos',
  'gestionar_cctv',
  'administrar_sistema',
  'ver_todos_los_clientes',
  'ocultar_cliente_demo',
  'cambiar_logo_cliente',
] as const;

export type Capacidad = (typeof CAPACIDADES)[number];

/**
 * Matriz de capacidades por rol.
 *
 * Refleja la visibilidad de menú de la aplicación anterior y, para las
 * acciones de escritura, el patrón de las políticas RLS
 * (`superadmin OR (admin_cliente AND cliente_id = caller)`): el técnico lee
 * configuración pero no la modifica.
 */
const MATRIZ: Readonly<Record<RolCanonico, readonly Capacidad[]>> = {
  cliente_final: [
    'ver_dashboard',
    'ver_alarmas',
    'ver_monitoreo',
    'ver_control',
    'ver_energia',
    'ver_gps',
    'ver_camaras',
    /*
     * Accionar equipos, pero sólo si su perfil lo tiene concedido.
     *
     * Estar en esta lista no basta: `puede()` comprueba después la bandera
     * `puede_usar_actuadores` del usuario, que llega apagada. Es decir, el rol
     * abre la puerta y el administrador decide a quién se la deja pasar, uno a
     * uno, desde Usuarios → Permisos.
     *
     * Antes ni siquiera estaba la puerta: el interruptor existía en la ficha
     * del usuario pero encenderlo no hacía nada, porque el rol descartaba la
     * capacidad antes de llegar a mirar la bandera.
     */
    'operar_actuadores',
  ],

  tecnico: [
    'ver_dashboard',
    'ver_alarmas',
    'ver_monitoreo',
    'ver_control',
    'ver_energia',
    'ver_gps',
    'ver_camaras',
    'ver_umbrales',
    'ver_automatizacion',
    'ver_en_ruta',
    'ver_contactos',
    'ver_escalamiento',
    'reconocer_alarmas',
    'resolver_alarmas',
  ],

  admin_cliente: [
    'ver_dashboard',
    'ver_alarmas',
    'ver_monitoreo',
    'ver_control',
    'ver_energia',
    'ver_gps',
    'ver_camaras',
    'ver_clientes',
    'ver_areas',
    'ver_nodos',
    'ver_sensores',
    'ver_actuadores',
    'ver_medidores',
    'ver_grabadores',
    'ver_vistas',
    'gestionar_cctv',
    'ver_umbrales',
    'ver_automatizacion',
    'ver_rutas',
    'ver_gps_usuarios',
    'ver_en_ruta',
    'ver_contactos',
    'ver_escalamiento',
    'ver_usuarios',
    'ver_reportes_generados',
    'reconocer_alarmas',
    'resolver_alarmas',
    'gestionar_inventario',
    'gestionar_umbrales',
    'gestionar_rutas',
    'gestionar_usuarios',
    'gestionar_notificaciones',
    'cambiar_logo_cliente',
  ],

  // El superadministrador recibe todas las capacidades (ver `capacidadesDe`).
  superadmin: [],
};

/** Capacidades efectivas de un rol. */
function capacidadesDe(rol: RolCanonico): readonly Capacidad[] {
  return rol === 'superadmin' ? CAPACIDADES : MATRIZ[rol];
}

/**
 * Decide qué puede hacer un perfil concreto.
 *
 * Combina el rol (matriz de arriba) con las banderas individuales del perfil,
 * que pueden restringir aún más lo que el rol permitiría.
 */
export class PoliticaAcceso {
  private readonly capacidades: ReadonlySet<Capacidad>;

  constructor(private readonly perfil: Perfil) {
    this.capacidades = new Set(capacidadesDe(perfil.rol.canonico));
  }

  puede(capacidad: Capacidad): boolean {
    if (!this.capacidades.has(capacidad)) return false;

    // Banderas individuales que restringen por encima del rol.
    if (capacidad === 'operar_actuadores') return this.puedeOperarActuadores;

    return true;
  }

  puedeAlguna(...capacidades: Capacidad[]): boolean {
    return capacidades.some((c) => this.puede(c));
  }

  /**
   * Operar actuadores exige la bandera del perfil, salvo para el
   * superadministrador, que siempre puede.
   */
  get puedeOperarActuadores(): boolean {
    if (this.perfil.rol.esSuperadmin) return true;
    return this.perfil.puedeUsarActuadores;
  }

  /**
   * Ver la ubicación de otros trabajadores.
   * Sin esta bandera el módulo de GPS sigue siendo accesible, pero el usuario
   * sólo ve su propia posición.
   */
  get puedeVerUbicacionesAjenas(): boolean {
    if (this.perfil.rol.esSuperadmin) return true;
    return this.perfil.puedeVerUbicaciones;
  }

  /** Explica por qué una acción está deshabilitada, para mostrarlo en un tooltip. */
  motivoDenegacion(capacidad: Capacidad): string | null {
    if (this.puede(capacidad)) return null;

    if (capacidad === 'operar_actuadores' && this.capacidades.has(capacidad)) {
      return 'Tu usuario no tiene habilitado el control de actuadores';
    }
    return 'Tu rol no tiene permiso para esta acción';
  }
}
